import React, { useState } from 'react';
import axios from 'axios';
import * as Icons from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import { SERVICES } from '../mock/mock';
import { PageHero } from './BuyABook';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Services = () => {
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast({ title: 'Please enter a valid email.', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/newsletter`, { email });
      toast({ title: 'Subscribed!', description: data.message });
      setEmail('');
    } catch (err) {
      toast({ title: 'Could not subscribe.', description: 'Please try again.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fade-in">
      <PageHero
        title="Our Services"
        subtitle="A commitment to going beyond the pages — a comprehensive suite of professional services for book enthusiasts, researchers, and writers."
      />

      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h2 className="font-serif text-3xl font-bold text-brand-ink">A passion for knowing more…</h2>
          <div className="section-divider mx-auto mt-4" />
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {SERVICES.map((s) => {
            const Ico = Icons[s.icon] || Icons.BookOpen;
            return (
              <div
                key={s.title}
                className="group bg-white rounded-2xl p-8 shadow-md hover:shadow-xl transition-all duration-500 hover:-translate-y-1 border border-brand-goldlight/40"
              >
                <div className="h-14 w-14 rounded-xl bg-sand flex items-center justify-center mb-5 group-hover:bg-brand-gold transition-colors duration-500">
                  <Ico className="h-7 w-7 text-brand-brass group-hover:text-white transition-colors duration-500" />
                </div>
                <h3 className="font-serif text-xl font-bold text-brand-ink mb-3">{s.title}</h3>
                <p className="text-sm text-brand-brown/80 leading-relaxed">{s.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Resources */}
      <section className="bg-sand/50">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl font-bold text-brand-ink">An array of resources</h2>
            <div className="section-divider mx-auto mt-4" />
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-md">
              <h3 className="font-serif text-2xl font-bold text-brand-ink mb-4">Limpid Reader App</h3>
              <ul className="space-y-3">
                {['Collaborate with fellow readers.', 'Showcase your projects.', 'Experience the world of reading.'].map((t) => (
                  <li key={t} className="flex items-center gap-3 text-brand-brown/90">
                    <Icons.CheckCircle2 className="h-5 w-5 text-brand-gold" /> {t}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-md">
              <h3 className="font-serif text-2xl font-bold text-brand-ink mb-4">Newsletter</h3>
              <ul className="space-y-3">
                {['A world of thought-provoking articles.', 'Case studies that celebrate literature.', 'Exclusive access to better understanding.'].map((t) => (
                  <li key={t} className="flex items-center gap-3 text-brand-brown/90">
                    <Icons.CheckCircle2 className="h-5 w-5 text-brand-gold" /> {t}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="text-center mt-12">
            <h3 className="font-serif text-2xl font-bold text-brand-ink">Write, Read, Understand</h3>
            <p className="text-brand-brass font-body text-lg mt-2">Join 900+ Readers</p>
            <p className="text-brand-brown/70 mt-1">Stay in the loop with everything you need to know.</p>
            <form onSubmit={subscribe} className="mt-6 flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                className="bg-white border-brand-goldlight h-12 rounded-full px-5"
              />
              <Button
                type="submit"
                disabled={loading}
                className="bg-brand-ink hover:bg-brand-brown text-cream rounded-full h-12 px-8 gap-2 shrink-0"
              >
                <Icons.Send className="h-4 w-4" /> {loading ? 'Joining...' : 'Subscribe'}
              </Button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
